import {
    styled,
    SxProps,
    TextareaAutosize,
    Theme,
    Typography,
} from '@mui/material';
import { Label } from './TextField';
import React from 'react';

const Input = styled(TextareaAutosize, {
    shouldForwardProp: prop =>
        prop !== '$variant' && prop !== '$white' && prop !== '$error',
})<{
    $variant: 'standard' | 'filled';
    $white?: boolean;
    $error?: boolean;
}>(({ theme, $variant, $white, $error }) => ({
    'width': '100%',

    'resize': 'none',

    'color': theme.palette.text.primary,
    'fontSize': theme.typography.body1.fontSize,
    'fontFamily': theme.typography.fontFamily,
    'fontWeight': 400,
    'lineHeight': '1.5rem',

    'borderRadius': theme.shape.borderRadius * 2,
    'padding': 16,

    ...($variant === 'standard' && {
        border: `1px solid ${
            $error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.23)'
        }`,
        backgroundColor: theme.palette.background.paper,
    }),

    ...($variant === 'filled' && {
        border: `none`,
        backgroundColor: $white ? 'white' : theme.palette.grey[100],
    }),

    '&:focus': {
        ...($variant === 'standard' && {
            padding: 15, // correct padding to match the border width
            borderColor: $error
                ? theme.palette.error.main
                : theme.palette.primary.main,
            borderWidth: '2px',
        }),
    },

    '&:disabled': {
        color: theme.palette.text.disabled,
        backgroundColor: theme.palette.grey[50],
        fontWeight: 500,
    },
}));

const Base = styled('div', {
    shouldForwardProp: prop => prop !== 'width' && prop !== 'fullWidth',
})<{
    width: string | number;
    fullWidth?: boolean;
}>(({ width, fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: fullWidth ? '100%' : width,
    maxWidth: fullWidth ? '100%' : width,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
}));

export interface TextAreaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
    // Core input properties
    onChange?: (
        value: string,
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => void;
    placeholder?: string;
    label?: string;
    helperText?: React.ReactNode;

    // Behavior
    variant?: 'standard' | 'filled';
    disabled?: boolean;
    required?: boolean;
    error?: boolean;

    // Row control
    rows?: number;
    minRows?: number;
    maxRows?: number;

    // Layout & Styling
    width?: string | number;
    fullWidth?: boolean;
    white?: boolean;

    sx?: SxProps<Theme>;
    inputSx?: SxProps<Theme>;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
        {
            onChange,
            label,
            rows,
            maxRows,
            minRows = 1,
            variant = 'standard',
            required,
            helperText,
            error,
            width = '300px',
            fullWidth,
            white,
            sx,
            inputSx,
            ...props
        },
        ref
    ) => (
        <Base width={width} fullWidth={fullWidth} sx={sx}>
            {label && <Label text={required ? label + ' *' : label} />}
            <Input
                $variant={variant}
                $white={white}
                $error={error}
                {...props}
                ref={ref}
                onChange={e => onChange?.(e.target.value, e)}
                maxRows={rows ?? maxRows}
                minRows={rows ?? minRows}
                required={required}
                sx={inputSx}
            />
            {helperText && (
                <Typography
                    variant='caption'
                    color={error ? 'error' : 'textSecondary'}
                    sx={{
                        paddingLeft: 2,
                        paddingTop: 0.25,
                        userSelect: 'none',
                    }}
                    component={'div'}>
                    {helperText}
                </Typography>
            )}
        </Base>
    )
);

TextArea.displayName = 'TextArea';

export default TextArea;
